import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Routes, Route, useLocation } from 'react-router-dom';
import { useCustomerAuth } from './contexts/CustomerAuthContext';
import CustomerShell from './CustomerShell';
import OrderDetails from './OrderDetails';
import CustomerProfile from './CustomerProfile';
import CustomerAddresses from './CustomerAddresses';
import CustomerOrders from './CustomerOrders';
import CustomerSupport from './CustomerSupport';
import CustomerNotifications from './CustomerNotifications';
import CustomerPromotions from './CustomerPromotions';
import CustomerAnalytics from './CustomerAnalytics';
import RefundRequest from './components/RefundRequest';
import Chatbot from './components/Chatbot';
import AIInsights from './components/AIInsights';
import { getProductUrl } from './config/platformConfig';
import { LayoutGrid, ShoppingBag, DollarSign, Heart, Package, X, Eye, Tag, CheckCircle, XCircle, Download, Clock, Key, ExternalLink, ArrowRight, Mail } from 'lucide-react';
import './CustomerDashboard.css';
import './styles/dashboard-overview.css';
import './styles/customer-wishlist.css';
import './styles/customer-downloads.css';

const CustomerDashboard = () => {
  const { customer, isAuthenticated, loading, refreshUser, logout } = useCustomerAuth();
  const [overview, setOverview] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  // Debug authentication state
  console.log('CustomerDashboard auth state:', { isAuthenticated, customer: !!customer, customerId: customer?.id });

  const fetchDashboardData = useCallback(async () => {
    try {
      const response = await fetch('/api/customer/dashboard/overview', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setOverview(data.overview);
      } else if (response.status === 401) {
        console.log('Customer dashboard: 401 error, logging out');
        logout();
      } else {
        console.log('Customer dashboard: Non-401 error, status:', response.status);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      // Don't logout on network errors, just show error state
    } finally {
      setDashboardLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    const run = async () => {
      // Wait for authentication initialization to complete
      if (loading) {
        console.log('CustomerDashboard: Still loading authentication...');
        return;
      }

      // Check authentication status
      if (!isAuthenticated) {
        console.log('CustomerDashboard: User not authenticated, redirecting to login');
        const currentPath = location.pathname;
        navigate(`/login?redirect=${encodeURIComponent(currentPath)}`, { replace: true });
        return;
      }

      // User is authenticated, fetch dashboard data
      console.log('CustomerDashboard: User is authenticated, fetching dashboard data');
      await fetchDashboardData();
    };
    run();
  }, [isAuthenticated, loading, fetchDashboardData, navigate, location.pathname]);

  const handleLogout = () => logout();

  if (loading) {
    return (
      <div className="customer-loading" style={{ minHeight: '100vh' }}>
        <div className="customer-spinner" />
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="customer-loading" style={{ minHeight: '100vh', flexDirection: 'column', gap: '12px' }}>
        <h2 style={{ margin: 0, color: 'var(--c-text)', fontSize: 'var(--c-fs-lg)', fontWeight: 600 }}>
          Unable to load dashboard
        </h2>
        <p style={{ margin: 0, color: 'var(--c-text-muted)', fontSize: 'var(--c-fs-md)' }}>
          Please try logging in again.
        </p>
        <button
          type="button"
          className="customer-btn"
          onClick={() => navigate('/login')}
          style={{ marginTop: 8 }}
        >
          Go to Login
        </button>
      </div>
    );
  }

  return (
    <>
      <CustomerShell>
        <Routes>
          <Route index element={<OverviewTab overview={overview} customer={customer} />} />
          <Route path="orders" element={<CustomerOrders />} />
          <Route path="orders/:orderId" element={<OrderDetails />} />
          <Route path="downloads" element={<DigitalLibraryTab />} />
          <Route path="cart" element={<CartTab />} />
          <Route path="wishlist" element={<WishlistTab />} />
          <Route path="promotions" element={<CustomerPromotions />} />
          <Route path="addresses" element={<CustomerAddresses />} />
          <Route path="refunds" element={<RefundRequest />} />
          <Route path="profile" element={<CustomerProfile />} />
          <Route path="notifications" element={<CustomerNotifications />} />
          <Route path="analytics" element={<CustomerAnalytics />} />
          {/* Support removed */}
        </Routes>
      </CustomerShell>
      {/* Render chatbot only on overview page - outside CustomerShell */}
      <Chatbot />
    </>
  );
};

// Email Verification Banner — compact, calm, brand-aligned.
const EmailVerificationBanner = ({ email }) => {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleResend = async () => {
    setSending(true);
    try {
      const response = await fetch('/api/customer/auth/resend-verification', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      if (response.ok) {
        setSent(true);
        setTimeout(() => setSent(false), 5000);
      }
    } catch (error) {
      console.error('Resend verification error:', error);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="dboard-banner" role="status">
      <Mail size={16} className="dboard-banner__icon" />
      <span className="dboard-banner__text">Verify your email to secure your account.</span>
      {sent ? (
        <span className="dboard-banner__sent">Sent — check your inbox</span>
      ) : (
        <button
          type="button"
          className="dboard-banner__btn"
          onClick={handleResend}
          disabled={sending}
        >
          {sending ? 'Sending…' : 'Resend'}
        </button>
      )}
    </div>
  );
};

// Overview Tab — clean compact dashboard. Class prefix .dboard.
const OverviewTab = ({ overview, customer }) => {
  const navigate = useNavigate();

  if (!overview) {
    return (
      <div className="dboard">
        <div className="dboard__skel dboard__skel--head" />
        <div className="dboard__stats">
          {[0, 1].map(i => <div key={i} className="dboard__skel dboard__skel--stat" />)}
        </div>
        <div className="dboard__skel dboard__skel--list" />
      </div>
    );
  }

  const firstName = customer?.firstName || 'there';
  const totalOrders = overview.totalOrders || 0;
  const totalSpent = new Intl.NumberFormat('en-NG', {
    style: 'currency', currency: 'NGN', minimumFractionDigits: 2, maximumFractionDigits: 2
  }).format(parseFloat(overview.totalSpent) || 0);

  const statusClass = (status) => `dboard-pill dboard-pill--${(status || 'pending').toLowerCase()}`;

  return (
    <div className="dboard">
      {/* Email verification banner */}
      {overview.isEmailVerified === false && (
        <EmailVerificationBanner email={overview.customerEmail || customer?.email} />
      )}

      {/* Greeting */}
      <header className="dboard__head">
        <h1 className="dboard__title">Hi, {firstName}</h1>
        <p className="dboard__sub">Here's a quick look at your account.</p>
      </header>

      {/* Stat strip */}
      <section className="dboard__stats" aria-label="Account summary">
        <div className="dboard-stat">
          <div className="dboard-stat__head">
            <span className="dboard-stat__icon"><ShoppingBag size={16} /></span>
            <span className="dboard-stat__label">Orders</span>
          </div>
          <div className="dboard-stat__value">{totalOrders}</div>
          <div className="dboard-stat__foot">{totalOrders ? 'Lifetime total' : 'No orders yet'}</div>
        </div>
        <div className="dboard-stat">
          <div className="dboard-stat__head">
            <span className="dboard-stat__icon"><DollarSign size={16} /></span>
            <span className="dboard-stat__label">Total spent</span>
          </div>
          <div className="dboard-stat__value dboard-stat__value--money">{totalSpent}</div>
          <div className="dboard-stat__foot">All time</div>
        </div>
      </section>

      {/* Recent orders */}
      <section className="dboard__card" aria-label="Recent orders">
        <header className="dboard__card-head">
          <h2 className="dboard__card-title">
            <Package size={16} aria-hidden />
            Recent orders
          </h2>
          <button type="button" className="dboard__link-btn" onClick={() => navigate('/dashboard/orders')}>
            View all <ArrowRight size={14} />
          </button>
        </header>

        {overview.recentOrders && overview.recentOrders.length > 0 ? (
          <ul className="dboard-orders">
            {overview.recentOrders.slice(0, 5).map((order, idx) => {
              const orderId = order.orderNumber || (order.id ? order.id.slice(-8).toUpperCase() : '');
              const date = order.createdAt
                ? new Date(order.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                : '';
              const total = new Intl.NumberFormat('en-NG', {
                style: 'currency', currency: 'NGN', minimumFractionDigits: 2, maximumFractionDigits: 2
              }).format(parseFloat(order.total) || 0);
              return (
                <li
                  key={order.id || idx}
                  className="dboard-orders__row"
                  onClick={() => navigate(`/dashboard/orders/${order.id}`)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate(`/dashboard/orders/${order.id}`); }}
                >
                  <div className="dboard-orders__id">#{orderId}</div>
                  <div className="dboard-orders__date">{date}</div>
                  <span className={statusClass(order.status)}>{order.status || 'Pending'}</span>
                  <div className="dboard-orders__total">{total}</div>
                  <Eye size={16} className="dboard-orders__eye" aria-hidden />
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="dboard__empty">
            <Package size={28} aria-hidden />
            <div>
              <div className="dboard__empty-title">No recent orders</div>
              <div className="dboard__empty-sub">Start shopping to see them here.</div>
            </div>
          </div>
        )}
      </section>

      {/* Quick links */}
      <section className="dboard__quick" aria-label="Quick links">
        <button type="button" className="dboard-quick" onClick={() => navigate('/dashboard/wishlist')}>
          <Heart size={16} />
          <span>Wishlist</span>
        </button>
        <button type="button" className="dboard-quick" onClick={() => navigate('/dashboard/downloads')}>
          <Download size={16} />
          <span>Downloads</span>
        </button>
        <button type="button" className="dboard-quick" onClick={() => navigate('/dashboard/promotions')}>
          <Tag size={16} />
          <span>Promotions</span>
        </button>
        <button type="button" className="dboard-quick" onClick={() => navigate('/dashboard/profile')}>
          <LayoutGrid size={16} />
          <span>Profile</span>
        </button>
      </section>
    </div>
  );
};

// Orders Tab Component
const OrdersTab = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await fetch('/api/customer/order-tracking', {
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include' // Include cookies
      });

      if (response.ok) {
        const data = await response.json();
        setOrders(data.orders);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div>Loading orders...</div>;

  return (
    <div className="orders-tab">
      <h2>Order History</h2>
      {orders.length > 0 ? (
        <div className="orders-list">
          {orders.map((order) => (
            <div key={order._id} className="order-card">
              <div className="order-header">
                <h3>#{order.orderNumber}</h3>
                <span className={`status-badge ${order.status}`}>
                  {order.status}
                </span>
              </div>
              <div className="order-details">
                <p>Date: {new Date(order.createdAt).toLocaleDateString()}</p>
                <p>Total: ₦{order.total.toLocaleString()}</p>
                <p>Items: {order.items.length}</p>
              </div>
              <div className="order-actions">
                <button className="btn-secondary">View Details</button>
                <button className="btn-primary">Reorder</button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p>No orders found</p>
      )}
    </div>
  );
};

// Cart Tab Component
const CartTab = () => {
  const [cart, setCart] = useState({ items: [], itemCount: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCart();
  }, []);

  const fetchCart = async () => {
    try {
      const response = await fetch('/api/customer/cart', {
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setCart({
          items: data.cart?.items || [],
          itemCount: data.cart?.itemCount || 0,
          total: data.cart?.totalPrice || 0
        });
      }
    } catch (error) {
      console.error('Error fetching cart:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
        padding: '24px'
      }}>
        <div style={{ animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }}>
          <div style={{
            height: '32px',
            backgroundColor: '#e5e7eb',
            borderRadius: '8px',
            width: '256px',
            marginBottom: '24px'
          }}></div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
      padding: '24px'
    }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          marginBottom: '32px'
        }}>
          <div style={{
            padding: '12px',
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            borderRadius: '12px',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
          }}>
            <ShoppingBag style={{ width: '32px', height: '32px', color: 'white' }} />
          </div>
          <h1 style={{
            fontSize: '30px',
            fontWeight: '700',
            color: '#111827',
            margin: 0
          }}>Shopping Cart ({cart.itemCount})</h1>
        </div>

        {cart.items.length > 0 ? (
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: '24px'
          }}>
            {/* Cart Items */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
              border: '1px solid #e5e7eb',
              overflow: 'hidden'
            }}>
              {cart.items.map((item) => (
                <div key={item.id} style={{
                  padding: '24px',
                  borderBottom: '1px solid #e5e7eb',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                >
                  <div style={{
                    display: 'flex',
                    gap: '24px',
                    alignItems: 'center'
                  }}>
                    <div style={{
                      width: '100px',
                      height: '100px',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      flexShrink: 0,
                      backgroundColor: '#f3f4f6'
                    }}>
                      {item.product?.images?.[0] ? (
                        <img
                          src={item.product.images[0]}
                          alt={item.product.name}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                      ) : (
                        <div style={{
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#9ca3af'
                        }}>No Image</div>
                      )}
                    </div>
                    <div style={{ flex: 1 }}>
                      <h3 style={{
                        fontSize: '18px',
                        fontWeight: '600',
                        color: '#111827',
                        margin: '0 0 8px 0'
                      }}>{item.product?.name || 'Product not available'}</h3>
                      <p style={{
                        fontSize: '16px',
                        fontWeight: '600',
                        color: '#059669',
                        margin: 0
                      }}>₦{item.product?.price?.toLocaleString() || '0'}</p>
                    </div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '8px',
                      backgroundColor: '#f9fafb',
                      borderRadius: '8px'
                    }}>
                      <button style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '6px',
                        border: '1px solid #e5e7eb',
                        backgroundColor: 'white',
                        cursor: 'pointer',
                        fontSize: '18px',
                        fontWeight: '600',
                        color: '#6b7280'
                      }}>-</button>
                      <span style={{
                        fontSize: '16px',
                        fontWeight: '600',
                        color: '#111827',
                        minWidth: '24px',
                        textAlign: 'center'
                      }}>{item.quantity}</span>
                      <button style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '6px',
                        border: '1px solid #e5e7eb',
                        backgroundColor: 'white',
                        cursor: 'pointer',
                        fontSize: '18px',
                        fontWeight: '600',
                        color: '#6b7280'
                      }}>+</button>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <p style={{
                        fontSize: '18px',
                        fontWeight: '700',
                        color: '#111827',
                        margin: '0 0 8px 0'
                      }}>₦{item.itemTotal.toLocaleString()}</p>
                      <button style={{
                        padding: '6px 12px',
                        backgroundColor: '#fee2e2',
                        color: '#dc2626',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: 'pointer'
                      }}>Remove</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Cart Summary */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
              border: '1px solid #e5e7eb'
            }}>
              <h3 style={{
                fontSize: '20px',
                fontWeight: '600',
                color: '#111827',
                margin: '0 0 16px 0'
              }}>Order Summary</h3>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '12px 0',
                borderTop: '1px solid #e5e7eb'
              }}>
                <span style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#111827'
                }}>Total</span>
                <span style={{
                  fontSize: '18px',
                  fontWeight: '700',
                  color: '#111827'
                }}>₦{cart.total.toLocaleString()}</span>
              </div>
              <button
                style={{
                  width: '100%',
                  marginTop: '16px',
                  background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                  color: 'white',
                  padding: '12px 24px',
                  borderRadius: '12px',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                  transform: 'translateY(0)',
                  fontWeight: '600',
                  fontSize: '16px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)';
                  e.target.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.1)';
                  e.target.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)';
                  e.target.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1)';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                Proceed to Checkout
              </button>
            </div>
          </div>
        ) : (
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '48px',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
            border: '1px solid #e5e7eb',
            textAlign: 'center'
          }}>
            <div style={{
              padding: '16px',
              backgroundColor: '#f3f4f6',
              borderRadius: '50%',
              width: '80px',
              height: '80px',
              margin: '0 auto 24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <ShoppingBag style={{ width: '40px', height: '40px', color: '#9ca3af' }} />
            </div>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '500',
              color: '#111827',
              marginBottom: '8px',
              margin: 0
            }}>Your cart is empty</h3>
            <p style={{
              color: '#6b7280',
              margin: 0
            }}>Add items to your cart to see them here.</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Wishlist Tab Component
const WishlistTab = () => {
  const navigate = useNavigate();
  const [wishlist, setWishlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [processingItems, setProcessingItems] = useState(new Set());

  useEffect(() => {
    fetchWishlist();
  }, []);

  const fetchWishlist = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/customer/wishlist', {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setWishlist(data.wishlist || []);
      } else {
        throw new Error('Failed to fetch wishlist');
      }
    } catch (err) {
      setError('Failed to load wishlist');
    } finally {
      setLoading(false);
    }
  };

  const removeFromWishlist = async (productId) => {
    try {
      setProcessingItems(prev => new Set(prev).add(productId));
      const response = await fetch(`/api/customer/wishlist/${productId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        setWishlist(prev => prev.filter(item => item.id !== productId));
        setSuccess('Removed from wishlist');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        throw new Error('Failed to remove item');
      }
    } catch (err) {
      setError('Failed to remove item from wishlist');
      setTimeout(() => setError(''), 3000);
    } finally {
      setProcessingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(productId);
        return newSet;
      });
    }
  };

  const addToCart = async (product) => {
    try {
      setProcessingItems(prev => new Set(prev).add(product.id));
      const productUrl = getProductUrl(product);
      if (productUrl) {
        setSuccess('Redirecting to product page...');
        setTimeout(() => {
          window.open(productUrl, '_blank');
          setSuccess('');
          setProcessingItems(prev => {
            const newSet = new Set(prev);
            newSet.delete(product.id);
            return newSet;
          });
        }, 500);
      } else {
        throw new Error('Store information not available');
      }
    } catch (err) {
      setError('Failed to open product page');
      setTimeout(() => setError(''), 3000);
      setProcessingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(product.id);
        return newSet;
      });
    }
  };

  const viewProduct = (product) => {
    const productUrl = getProductUrl(product);
    if (productUrl) {
      window.open(productUrl, '_blank');
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN'
    }).format(price);
  };

  const calculateDiscount = (price, compareAtPrice) => {
    if (!compareAtPrice || compareAtPrice <= price) return null;
    const discount = ((compareAtPrice - price) / compareAtPrice) * 100;
    return Math.round(discount);
  };

  if (loading) {
    return (
      <div className="cwish">
        <div className="cwish__skel cwish__skel--head" />
        <div className="cwish__grid">
          {[0, 1, 2, 3].map(i => (
            <div key={i} className="cwish__skel cwish__skel--card" />
          ))}
        </div>
      </div>
    );
  }

  const totalValue = wishlist.reduce((sum, item) => sum + (parseFloat(item.price) || 0), 0);

  return (
    <div className="cwish">
      <header className="cwish__head">
        <h1 className="cwish__title">Wishlist</h1>
        <p className="cwish__sub">Save your favourite products and shop them later.</p>
      </header>

      {error && (
        <div className="cwish__alert cwish__alert--error" role="alert">
          <XCircle size={14} aria-hidden /> <span>{error}</span>
        </div>
      )}
      {success && (
        <div className="cwish__alert cwish__alert--success" role="status">
          <CheckCircle size={14} aria-hidden /> <span>{success}</span>
        </div>
      )}

      {wishlist.length > 0 && (
        <section className="cwish__stats" aria-label="Wishlist summary">
          <div className="cwish-stat">
            <div className="cwish-stat__head">
              <span className="cwish-stat__icon"><Heart size={14} /></span>
              <span className="cwish-stat__label">Items</span>
            </div>
            <div className="cwish-stat__value">{wishlist.length}</div>
          </div>
          <div className="cwish-stat">
            <div className="cwish-stat__head">
              <span className="cwish-stat__icon"><Package size={14} /></span>
              <span className="cwish-stat__label">Total value</span>
            </div>
            <div className="cwish-stat__value cwish-stat__value--money">{formatPrice(totalValue)}</div>
          </div>
        </section>
      )}

      {wishlist.length === 0 ? (
        <div className="cwish__empty">
          <Heart size={32} aria-hidden />
          <div>
            <div className="cwish__empty-title">Your wishlist is empty</div>
            <div className="cwish__empty-sub">Tap the heart on any product to save it here.</div>
          </div>
          <button
            type="button"
            className="cwish__empty-btn"
            onClick={() => navigate('/dashboard')}
          >
            <ShoppingBag size={14} /> Start shopping
          </button>
        </div>
      ) : (
        <ul className="cwish__grid">
          {wishlist.map((product) => {
            const discount = calculateDiscount(product.price, product.compareAtPrice);
            const isProcessing = processingItems.has(product.id);
            const outOfStock = product.quantity === 0;

            return (
              <li key={product.id} className="cwish-card">
                {discount && <span className="cwish-card__badge">-{discount}%</span>}
                <button
                  type="button"
                  onClick={() => removeFromWishlist(product.id)}
                  disabled={isProcessing}
                  className="cwish-card__remove"
                  aria-label="Remove from wishlist"
                >
                  <X size={14} aria-hidden />
                </button>

                <button
                  type="button"
                  className="cwish-card__img"
                  onClick={() => viewProduct(product)}
                  aria-label={`View ${product.name}`}
                >
                  <img
                    src={product.primaryImage || '/placeholder-product.jpg'}
                    alt={product.name}
                    onError={(e) => { e.target.src = '/placeholder-product.jpg'; }}
                  />
                  {outOfStock && <span className="cwish-card__oos">Out of stock</span>}
                </button>

                <div className="cwish-card__body">
                  <button
                    type="button"
                    className="cwish-card__name"
                    onClick={() => viewProduct(product)}
                  >
                    {product.name}
                  </button>
                  {product.category && (
                    <div className="cwish-card__cat">{product.category}</div>
                  )}
                  <div className="cwish-card__price-row">
                    <span className="cwish-card__price">{formatPrice(product.price)}</span>
                    {product.compareAtPrice && product.compareAtPrice > product.price && (
                      <span className="cwish-card__compare">{formatPrice(product.compareAtPrice)}</span>
                    )}
                  </div>
                  <div className="cwish-card__actions">
                    <button
                      type="button"
                      onClick={() => addToCart(product)}
                      disabled={isProcessing || outOfStock}
                      className="cwish-card__cta"
                    >
                      {isProcessing
                        ? 'Loading…'
                        : outOfStock
                          ? 'Out of stock'
                          : (
                            <>
                              <ShoppingBag size={14} aria-hidden /> Add to cart
                            </>
                          )}
                    </button>
                    <button
                      type="button"
                      onClick={() => viewProduct(product)}
                      className="cwish-card__view"
                      aria-label="View product"
                    >
                      <Eye size={14} aria-hidden />
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

// Digital Library Tab Component
const DigitalLibraryTab = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    fetchDigitalLibrary();
  }, []);

  const fetchDigitalLibrary = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/digital/library', {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setProducts(data.products || []);
      } else {
        throw new Error('Failed to fetch digital library');
      }
    } catch (err) {
      console.error('Error fetching digital library:', err);
      setError('Failed to load your digital library');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (accessToken, productName) => {
    setDownloading(accessToken);
    try {
      const response = await fetch(`/api/digital/download/${accessToken}`);
      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Download failed');
      }

      if (data.downloadUrl) {
        window.open(data.downloadUrl, '_blank');
        await fetchDigitalLibrary();
      }
    } catch (err) {
      console.error('Download error:', err);
      setError(err.message || 'Failed to process download');
      setTimeout(() => setError(''), 5000);
    } finally {
      setDownloading(null);
    }
  };

  const copyLicenseKey = (key) => {
    navigator.clipboard.writeText(key);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="cdown">
        <div className="cdown__skel cdown__skel--head" />
        <div className="cdown__grid">
          {[0, 1, 2].map(i => (
            <div key={i} className="cdown__skel cdown__skel--card" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="cdown">
      <header className="cdown__head">
        <h1 className="cdown__title">Downloads</h1>
        <p className="cdown__sub">
          {products.length > 0
            ? `${products.length} digital ${products.length === 1 ? 'product' : 'products'}`
            : 'Your purchased digital products show up here.'}
        </p>
      </header>

      {error && (
        <div className="cdown__alert cdown__alert--error" role="alert">
          <XCircle size={14} aria-hidden /> <span>{error}</span>
        </div>
      )}

      {products.length === 0 ? (
        <div className="cdown__empty">
          <Download size={32} aria-hidden />
          <div>
            <div className="cdown__empty-title">No digital products yet</div>
            <div className="cdown__empty-sub">Buy a digital product and the file will land here.</div>
          </div>
        </div>
      ) : (
        <ul className="cdown__grid">
          {products.map((product) => {
            const isExpired = product.expiresAt && new Date() > new Date(product.expiresAt);
            const limitReached = product.maxDownloads && product.downloadCount >= product.maxDownloads;
            const canDownload = product.isActive && !isExpired && !limitReached;
            const isDownloading = downloading === product.accessToken;
            const statusLabel = isExpired
              ? 'Expired'
              : limitReached
                ? 'Limit reached'
                : !product.isActive
                  ? 'Revoked'
                  : 'Available';
            const statusKey = canDownload ? 'ok' : isExpired ? 'expired' : limitReached ? 'limit' : 'revoked';

            return (
              <li key={product.id} className="cdown-card">
                <div className="cdown-card__img">
                  {product.productImage
                    ? <img src={product.productImage} alt={product.productName} />
                    : <Download size={24} aria-hidden />}
                  <span className={`cdown-card__status cdown-card__status--${statusKey}`}>
                    {statusLabel}
                  </span>
                </div>

                <div className="cdown-card__body">
                  <h2 className="cdown-card__name">{product.productName}</h2>

                  <dl className="cdown-card__meta">
                    {product.maxDownloads && (
                      <div className="cdown-card__meta-row">
                        <dt><Download size={12} aria-hidden /> Downloads</dt>
                        <dd>{product.downloadCount} / {product.maxDownloads}</dd>
                      </div>
                    )}
                    {product.expiresAt && (
                      <div className={`cdown-card__meta-row ${isExpired ? 'is-expired' : ''}`}>
                        <dt><Clock size={12} aria-hidden /> {isExpired ? 'Expired on' : 'Expires'}</dt>
                        <dd>{formatDate(product.expiresAt)}</dd>
                      </div>
                    )}
                  </dl>

                  {product.licenseKey && (
                    <div className="cdown-card__code">
                      <div className="cdown-card__code-label">
                        <Key size={12} aria-hidden /> License key
                      </div>
                      <div className="cdown-card__code-row">
                        <code className="cdown-card__code-value">{product.licenseKey}</code>
                        <button
                          type="button"
                          onClick={() => copyLicenseKey(product.licenseKey)}
                          className="cdown-card__copy"
                        >
                          Copy
                        </button>
                      </div>
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={() => handleDownload(product.accessToken, product.productName)}
                    disabled={!canDownload || isDownloading}
                    className="cdown-card__cta"
                  >
                    {isDownloading
                      ? 'Downloading…'
                      : canDownload
                        ? (<><Download size={14} aria-hidden /> Download</>)
                        : (<><ExternalLink size={14} aria-hidden /> {statusLabel}</>)}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

// Addresses Tab Component
const AddressesTab = () => {
  return (
    <div className="addresses-tab">
      <h2>Address Book</h2>
      <p>Manage your billing and shipping addresses</p>
      <button className="btn-primary">Add New Address</button>
    </div>
  );
};

// Profile Tab Component
const ProfileTab = ({ customer }) => {
  return (
    <div className="profile-tab">
      <h2>Profile Settings</h2>
      <div className="profile-form">
        <div className="form-group">
          <label>First Name</label>
          <input type="text" defaultValue={customer.firstName} />
        </div>
        <div className="form-group">
          <label>Last Name</label>
          <input type="text" defaultValue={customer.lastName} />
        </div>
        <div className="form-group">
          <label>Email</label>
          <input type="email" defaultValue={customer.email} disabled />
        </div>
        <div className="form-group">
          <label>Phone</label>
          <input type="tel" defaultValue={customer.phone} />
        </div>
        <button className="btn-primary">Update Profile</button>
      </div>
    </div>
  );
};

// Support Tab Component
const SupportTab = () => {
  return (
    <div className="support-tab">
      <h2>Support & Help</h2>
      <div className="support-options">
        <button className="btn-primary">Create Support Ticket</button>
        <button className="btn-secondary">View FAQ</button>
        <button className="btn-secondary">Contact Us</button>
      </div>
    </div>
  );
};

export default CustomerDashboard;
